import { useMutation, useQuery } from "@apollo/react-hooks";
import { useState, useEffect } from "react";
import { getOwnedProducts } from "../queries";
import { useOpportunityAlert } from "./useOpportunityAlert";
import { useCreateOpportunity } from "./useCreateOpportunity";
import { totalPrice } from "../helpers";
import useTutorialsState from "../../../hooks/useTutorialsState";
import useTagAccount from "../../../hooks/useTagAccount";
import { useMixpanel } from "react-mixpanel-browser";
import currency from "currency-formatter";

export const useGetAvailableProducts = () => {
  const { data, loading } = useQuery(getOwnedProducts);
  const availableProducts =
    (!loading && !!data && !!data.stores && data.stores.ownedProducts) || [];

  return {
    loading,
    availableProducts,
    fixedFee: data && data.accounts && data.accounts[0].fixedFee,
    variableFee: data && data.accounts && data.accounts[0].variableFee
  };
};

export const useChangeOpportunity = ({
  onDismiss,
  opportunityFormProps,
  filters,
  productsLoading,
  id,
  shareOpportunity,
  fixedFee,
  variableFee
}) => {
  const [loading, setLoading] = useState(false);
  const [opportunityId, setOpportunityId] = useState("");
  const [showShare, setShowShare] = useState(false);

  const { getHasCompletedTutorial, completeTutorial } = useTutorialsState();
  const { tagAccount } = useTagAccount();
  const mixpanel = useMixpanel();

  const {
    resetForm,
    formHasAnInvalidField,
    amount,
    isPercentage,
    selectedProduct
  } = opportunityFormProps;

  const {
    isAlertOpen,
    onAlertCancel,
    onAlertConfirm,
    onDismissSidebar
  } = useOpportunityAlert({
    onDismiss,
    resetForm,
    opportunityId,
    setShowShare,
    setOpportunityId
  });

  useEffect(() => {
    if (shareOpportunity) {
      setShowShare(true);
      setOpportunityId(id);
    }
  }, [shareOpportunity]);

  const [createOpportunity] = useCreateOpportunity(
    opportunityFormProps,
    filters
  );

  const onSave = async () => {
    setLoading(true);
    try {
      const {
        data: {
          createOpportunity: { id }
        }
      } = await createOpportunity();

      const hasAddedOpportunity = getHasCompletedTutorial("Add Opportunity");

      if (!hasAddedOpportunity) {
        await completeTutorial({
          variables: { name: "Add Opportunity", version: 1 }
        });

        await tagAccount("Opportunity Created");
      }

      mixpanel.track("Opportunity Created", {
        "Opportunity ID": id,
        "Product ID": selectedProduct && selectedProduct.id,
        Commission: isPercentage
          ? `${amount}%`
          : `$${currency.format(amount, { format: "USD" })}`
      });

      setLoading(false);
      setOpportunityId(id);
      setShowShare(true);
    } catch (err) {
      setLoading(false);
    }
  };

  return {
    isAlertOpen,
    onAlertCancel,
    onAlertConfirm,
    onDismissSidebar,
    onSave,
    pageIsLoading: loading || productsLoading,
    formHasAnInvalidField:
      formHasAnInvalidField ||
      (selectedProduct &&
        !selectedProduct.isDonation &&
        totalPrice(
          selectedProduct.price,
          amount,
          isPercentage,
          fixedFee,
          variableFee
        ) < 0),
    isAmountInvalid:
      selectedProduct &&
      !selectedProduct.isDonation &&
      totalPrice(
        selectedProduct.price,
        amount,
        isPercentage,
        fixedFee,
        variableFee
      ) < 0,
    opportunityId,
    showShare,
    setShowShare
  };
};
